/**
 * Channel Console — Website module (A7Y-3776).
 *
 * Two layers, per the design confirmed with Yevhen: a headline strip that
 * answers "how is the website doing" in about five seconds, then a detail
 * section with the 30-day trend and the referrer breakdown.
 *
 * Five states this deliberately renders EXPLICITLY rather than as zeros:
 *
 *   - `configured: false` — the Mixpanel Worker secrets are absent in this
 *     environment. Distinct from "no traffic".
 *   - `matchedEvents === 0` with `queriedEvents > 0` — the upstream event name
 *     no longer matches. This is the failure three earlier attempts at this
 *     module would have shipped silently, and it is why the panel names the
 *     event it queried.
 *   - `complete: false` — part of the 30-day window is not aggregated yet. The
 *     trend plots only the days it has, because plotting a missing day as zero
 *     reads as a traffic collapse.
 *   - nothing aggregated at all (A7Y-3929) — the history holds no day in the
 *     window. Rendering the headline cards here would put four confident
 *     zeros in front of the CMO, which is the same class of lie as a wrong
 *     event name. A7Y-3929 split this on whether a background pass was in
 *     flight; with the pull moved to a nightly job (A7Y-3777) the honest
 *     split is whether a pull has ever succeeded, which `freshness` carries.
 *
 * A fifth state, `freshness.stale` (A7Y-3777), is additive rather than
 * exclusive with the four above: a failed nightly/on-demand pull never
 * clears history, so the page keeps showing the last good numbers with a
 * banner layered on top — never an early return.
 */
import type { ChannelConsoleWebsiteData } from './types';
import { Card, Notice, formatInt, formatLocal, formatPercent } from './ui';

export function WebsiteModule({
  data,
  loading,
}: {
  data: ChannelConsoleWebsiteData | undefined;
  loading: boolean;
}) {
  if (loading && !data) {
    return <div className="text-sm text-zinc-500">Loading website data…</div>;
  }
  if (!data) return null;

  if (!data.configured) {
    return (
      <Notice tone="warn" title="Website module not configured">
        The Mixpanel read credentials are not set as Worker secrets in this environment, so no
        traffic data can be fetched. This is a provisioning gap, not an absence of traffic — see
        A7Y-3800.
      </Notice>
    );
  }

  // A wrong event name renders as a loud panel, never as a plausible zero.
  if (data.matchedEvents === 0 && data.queriedEvents > 0) {
    return (
      <Notice tone="warn" title="No events matched">
        Queried <code className="font-mono">{data.pageviewEvent}</code> and found{' '}
        {formatInt(data.queriedEvents)} events in the window, none of which matched. The upstream
        event name has most likely changed.
      </Notice>
    );
  }

  // An empty window renders as its own state rather than a strip of zeros
  // (A7Y-3929). What separates "not filled in yet" from "stuck" is no longer
  // an in-flight warm pass — the GET path starts none — but whether a pull has
  // ever succeeded and whether the last one errored.
  if (data.trend.length === 0) {
    return data.freshness.lastError === null ? (
      <Notice tone="info" title="Window not pulled yet">
        No days are aggregated. The nightly pull fills the window on its own; Refresh pulls the
        three most recent missing days now.
      </Notice>
    ) : (
      <Notice tone="warn" title="Window not aggregated">
        No days are aggregated and the last pull failed. Refresh retries now.
        <div className="mt-1 font-mono text-xs text-red-700 dark:text-red-400">
          {data.freshness.lastError}
        </div>
      </Notice>
    );
  }

  const { headline, trend, topReferrers } = data;
  const trendValues = trend.map((p) => p.visitors);
  const maxVisitors = Math.max(1, ...trendValues);

  return (
    <div className="space-y-6">
      {data.freshness.stale && (
        <Notice tone="warn" title="Data is stale">
          {data.freshness.lastSuccessAt === null
            ? 'No successful pull yet.'
            : `Last successful pull ${formatLocal(data.freshness.lastSuccessAt)}.`}
          {data.freshness.lastError && (
            <div className="mt-1 font-mono text-xs text-red-700 dark:text-red-400">
              {data.freshness.lastError}
            </div>
          )}
        </Notice>
      )}

      {/* Layer 1 — the five-second scan. */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card
          title="Visitors (7d)"
          primary={formatInt(headline.visitors7d)}
          secondary="Marketing pages only"
          sparkline={trendValues.slice(-14)}
        />
        <Card
          title="Signup page views (7d)"
          primary={formatInt(headline.signupPageViews7d)}
          secondary="/signup"
        />
        <Card
          title="Visit → signup page"
          primary={formatPercent(headline.visitToSignupRate)}
          secondary="Share of marketing pageviews"
        />
        <Card
          title="Top referrer"
          primary={headline.topReferrer ?? '—'}
          secondary="Excludes our own domains"
        />
      </div>

      {!data.complete && (
        <Notice tone="info" title="Partial window">
          {data.daysMissing.length} of the last 30 days are not aggregated yet, so the trend below
          is shorter than 30 points. The nightly pull closes the gap over the following nights;
          Refresh pulls the three most recent missing days now.
        </Notice>
      )}

      {/* Layer 2 — the detail. */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Daily visitors</h3>
          <p className="mt-0.5 text-xs text-zinc-500">
            Last {trend.length} complete {trend.length === 1 ? 'day' : 'days'}, UTC
          </p>
          {/* The empty case never reaches here — it is answered above by
              "Building the window" / "Window not aggregated", which say WHY
              the trend is empty instead of leaving a bare line. */}
          <div className="mt-4 flex h-40 items-end gap-px" role="img" aria-label="Daily visitors">
            {trend.map((point) => (
              <div
                key={point.date}
                className="flex-1 rounded-t bg-blue-500/80 dark:bg-blue-400/80"
                style={{ height: `${Math.max(2, (point.visitors / maxVisitors) * 100)}%` }}
                title={`${point.date}: ${formatInt(point.visitors)} visitors`}
              />
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Top referrers</h3>
          <p className="mt-0.5 text-xs text-zinc-500">
            First-touch referring domain. Our own domains are excluded;{' '}
            <code className="font-mono">$direct</code> is not — it is genuinely most of the traffic.
          </p>
          {topReferrers.length === 0 ? (
            <p className="mt-4 text-sm text-zinc-500">No referrers in this window.</p>
          ) : (
            <table className="mt-4 w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
                  <th className="pb-2 font-medium">Domain</th>
                  <th className="pb-2 text-right font-medium">Visits</th>
                </tr>
              </thead>
              <tbody>
                {topReferrers.map((row) => (
                  <tr key={row.domain} className="border-t border-zinc-100 dark:border-zinc-800">
                    <td className="py-1.5 font-mono text-xs text-zinc-700 dark:text-zinc-300">
                      {row.domain}
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-zinc-900 dark:text-zinc-100">
                      {formatInt(row.visits)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
